import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFirestoreQuery } from '../../firebase/useFirestore'
import { sortGoalsByPosition } from '../../helpers/liveReportHelpers/publicPersonaState'

// AI-CONTEXT: See docs/features/livereport-per-deelnemer.md
// De doelen van één deelnemer, mét laad- en foutstatus. Nodig omdat de
// bestaande lezers (GoalsCard, Goals.jsx) een kale lijst krijgen: een
// geweigerde read is daar niet te onderscheiden van 'geen doelen', en dat
// moet de Doelen-widget in de rapportage wél kunnen zeggen.
// AI-CONTEXT: See docs/pitfalls.md#database-firestore-silent-listener-failure
//
// Geen orderBy op Position in de query: een doel zonder dat veld valt dan stil
// weg (docs/pitfalls.md#database-firestore-orderby-excludes-missing-field).
// Verwijderde doelen filtert useFirestoreQuery (ADR-008).

/**
 * @typedef {object} PersonaGoalsResult
 * @property {Array<object>|null} goals - Op Position; `null` zolang er nog niets is
 * @property {boolean} loading - Nog geen antwoord en geen fout
 * @property {{code?: string, message?: string}|null} error - De fout van de read
 */

/**
 * @param {string|null} personaId - De deelnemer; `null` = niet ophalen
 * @param {{ enabled?: boolean }} [options] - `enabled: false` haalt niets op
 *   (sjabloonmodus, module uit, of de openbare link die de doelen al heeft)
 * @returns {PersonaGoalsResult}
 */
const usePersonaGoals = (personaId, { enabled = true } = {}) => {
    const [error, setError] = useState(/** @type {{code?: string}|null} */ (null))

    // Een nieuwe deelnemer begint zonder oordeel.
    useEffect(() => { setError(null) }, [personaId])

    const onError = useCallback((queryError) => { setError(queryError) }, [])
    const docs = useFirestoreQuery('PersonaGoals', {
        where: [['PersonaID', '==', personaId || '']],
        enabled: enabled && Boolean(personaId),
        onError,
    })

    const goals = useMemo(
        () => (Array.isArray(docs)
            ? sortGoalsByPosition(docs)
            : null),
        [docs],
    )

    if (error) return { goals: null, loading: false, error }
    return { goals, loading: goals === null, error: null }
}

export default usePersonaGoals
